import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
    TopActionBar,
    BulkAddTagsModal,
    AssignMoveGroupModal,
    UploadMultipleSourcesModal,
    CsvMigrationsUploadModal,
    PreflightMonitor,
    RefreshSourcesModal,
} from '../../components';
import { Actions } from './actions';
import SourcesTable from './SourcesTable';
import { useCsvMigrationPreflight, useRefreshSources } from '../../hooks';
import { DOC_TYPES } from '../../constants/docLinks';
import './Sources.scss';

export const Sources = React.memo(() => {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [addSourceModalOpen, setAddSourceModalOpen] = useState(false);
    const [uploadSourcesModalOpen, setUploadSourcesModalOpen] = useState(false);
    const [csvMigrationModalOpen, setCsvMigrationModalOpen] = useState(false);
    const [csvPreflightModalOpen, setCsvPreflightModalOpen] = useState(false);
    const [refreshSourcesModalOpen, setRefreshSourcesModalOpen] = useState(false);
    const [addTagsModalOpen, setAddTagsModalOpen] = useState(false);
    const [assignMoveGroupModalOpen, setAssignMoveGroupModalOpen] = useState(false);
    const [selectedSourcesForTags, setSelectedSourcesForTags] = useState([]);
    const [selectedSourcesForMoveGroup, setSelectedSourcesForMoveGroup] = useState([]);
    const bulkDeleteTriggerRef = useRef(null);
    const bulkConfigureMigrationTriggerRef = useRef(null);
    const bulkInspectSourcesTriggerRef = useRef(null);
    const bulkConfigureInSituTriggerRef = useRef(null);
    const preflightTriggerRef = useRef(null); // Store preflight trigger from SourcesTable
    const refetchDataRef = useRef(null); // Store refetch function from SourcesTable

    const userOrg = sessionStorage.getItem('userOrg');

    // CSV Migration Preflight hook
    const {
        migrationProfile,
        preflightPayload,
        isLoadingProfile,
        isProcessing,
        startPreflight,
        handlePreflightComplete,
        reset: resetCsvPreflight,
        migrationContext,
    } = useCsvMigrationPreflight();

    // Refresh Sources hook
    const { startSourceRefresh, stopPolling } = useRefreshSources({
        userOrg,
        onSuccess: (newSourceIds) => {
            // Refresh the sources table
            if (refetchDataRef.current) {
                refetchDataRef.current();
            }
        },
        onError: (error) => {
            console.error('Error refreshing sources:', error);
        },
    });

    const handleSearch = useCallback((query) => {
        setSearchQuery(query);
    }, []);

    const handleBulkDelete = useCallback((sourceIds) => {
        // Trigger the bulk delete modal in SourcesTable
        if (bulkDeleteTriggerRef.current) {
            bulkDeleteTriggerRef.current(sourceIds);
        }
    }, []);

    const handleBulkConfigureMigration = useCallback((sourceIds) => {
        // Trigger the bulk configure migration dialog in SourcesTable
        if (bulkConfigureMigrationTriggerRef.current) {
            bulkConfigureMigrationTriggerRef.current(sourceIds);
        }
    }, []);

    const handleBulkInspectSources = useCallback((sourceIds) => {
        // Trigger the bulk inspect sources dialog in SourcesTable
        if (bulkInspectSourcesTriggerRef.current) {
            bulkInspectSourcesTriggerRef.current(sourceIds);
        }
    }, []);

    const handleBulkConfigureInSitu = useCallback((sourceIds) => {
        if (bulkConfigureInSituTriggerRef.current) {
            bulkConfigureInSituTriggerRef.current(sourceIds);
        }
    }, []);

    const handleBulkAddTags = useCallback((sourceIds) => {
        setSelectedSourcesForTags(sourceIds);
        setAddTagsModalOpen(true);
    }, []);

    const handleBulkAssignMoveGroup = useCallback((sourceIds) => {
        setSelectedSourcesForMoveGroup(sourceIds);
        setAssignMoveGroupModalOpen(true);
    }, []);

    const handleAddSource = useCallback(() => {
        setAddSourceModalOpen(true);
    }, []);

    const handleAddSourceModalClose = useCallback(() => {
        setAddSourceModalOpen(false);
    }, []);

    const handleUploadSources = useCallback(() => {
        setUploadSourcesModalOpen(true);
    }, []);

    const handleUploadSourcesModalClose = useCallback(() => {
        setUploadSourcesModalOpen(false);
    }, []);

    const handleRunCsvMigration = useCallback(() => {
        setCsvMigrationModalOpen(true);
    }, []);

    const handleCsvMigrationModalClose = useCallback(() => {
        setCsvMigrationModalOpen(false);
    }, []);

    const handleCsvMigrationSuccess = useCallback(
        (result) => {
            // Refresh sources table
            if (refetchDataRef.current) {
                refetchDataRef.current();
            }

            // Close CSV modal
            setCsvMigrationModalOpen(false);

            // Start React preflight flow instead of backbone
            if (result && result.migrationProfileID) {
                // Use setTimeout to match backbone timing
                setTimeout(() => {
                    startPreflight(result.migrationProfileID);
                }, 200);
            }
        },
        [startPreflight],
    );

    const handleUploadSourcesSuccess = useCallback((continuePayload) => {
        // Refresh sources table
        if (refetchDataRef.current) {
            refetchDataRef.current();
        }

        // If we have a preflight payload, trigger the preflight check directly
        if (continuePayload && continuePayload.preflightPayload && preflightTriggerRef.current) {
            const migrationContext = {
                applianceId: continuePayload.applianceID,
                targetAccount: continuePayload.targetCloudID,
                cloudAccount: {
                    id: continuePayload.targetCloudID,
                    cloud_type: continuePayload.cloudType,
                    name: 'Selected Target Cloud',
                    appliance: { id: continuePayload.applianceID },
                },
                downloadCSV: continuePayload.downloadCSV,
                csvDownloadPayload: continuePayload.downloadCSV
                    ? {
                          cloud_account: continuePayload.targetCloudID,
                          cloud_type: continuePayload.cloudType,
                          appliance_id: continuePayload.applianceID,
                          sources: continuePayload.sourceConfigIDs,
                          migration_method: 'os-based',
                      }
                    : null,
            };

            // Trigger preflight modal directly
            preflightTriggerRef.current(continuePayload.preflightPayload, migrationContext);
        }
    }, []);

    const handleAddTagsModalClose = useCallback(() => {
        setAddTagsModalOpen(false);
        setSelectedSourcesForTags([]);
    }, []);

    const handleAssignMoveGroupModalClose = useCallback(() => {
        setAssignMoveGroupModalOpen(false);
        setSelectedSourcesForMoveGroup([]);
    }, []);

    const handleBulkActionSuccess = useCallback(() => {
        // Clear selected rows after successful bulk action
        setSelectedRowKeys([]);
        // Refresh table data to show updated tags/move groups
        if (refetchDataRef.current) {
            refetchDataRef.current();
        }
    }, []);

    const handleDataRefreshNeeded = useCallback((refetchFunction) => {
        refetchDataRef.current = refetchFunction;
    }, []);

    // Effect to open preflight modal when payload is ready
    useEffect(() => {
        if (preflightPayload && migrationProfile && !csvPreflightModalOpen) {
            setCsvPreflightModalOpen(true);
        }
    }, [preflightPayload, migrationProfile, csvPreflightModalOpen]);

    // Handle CSV preflight completion
    const handleCsvPreflightComplete = useCallback(
        (result) => {
            setCsvPreflightModalOpen(false);
            if (result && result.success) {
                // Extract selected source configs from preflight results
                const selectedSourceConfigs = result.preflights?.filter((p) => p.type === 'source_config') || [];
                handlePreflightComplete(selectedSourceConfigs);
            }
            resetCsvPreflight();
        },
        [handlePreflightComplete, resetCsvPreflight],
    );

    // Handle CSV preflight cancellation
    const handleCsvPreflightCancel = useCallback(() => {
        setCsvPreflightModalOpen(false);
        resetCsvPreflight();
    }, [resetCsvPreflight]);

    const handleRefreshSources = useCallback(() => {
        setRefreshSourcesModalOpen(true);
    }, []);

    const handleRefreshSourcesModalClose = useCallback(() => {
        setRefreshSourcesModalOpen(false);
    }, []);

    const handleRefreshSourcesSuccess = useCallback(
        async (refreshParams) => {
            setRefreshSourcesModalOpen(false);

            // Get cloud account data for CSV download
            const cloudAccountData = {
                id: refreshParams.cloudAccountId,
                type: 'vsphere', // or get from the actual cloud account
                appliance: {
                    id: refreshParams.applianceId,
                },
            };

            // Start refresh with CSV download flag if requested
            // The useRefreshSources hook will automatically trigger CSV download after polling completes
            await startSourceRefresh(
                { ...refreshParams, shouldDownloadCSV: refreshParams.downloadCSV },
                cloudAccountData,
            );
        },
        [startSourceRefresh],
    );

    useEffect(() => {
        // Remove if backbone module will not be called
        return () => {
            if (window?.clearPreflightCheckPolling) {
                window?.clearPreflightCheckPolling();
            }
            if (window?.clearMigrationRefreshStateCheckPoling) {
                window?.clearMigrationRefreshStateCheckPoling();
            }
        };
        // Stop source refresh polling on unmount
        if (stopPolling) {
            stopPolling();
        }
    }, [stopPolling]);

    return (
        <div className={'sources-page'}>
            <TopActionBar showDocumentationLinksDropdown={true} documentationType={DOC_TYPES.SOURCES}>
                <Actions
                    onSearch={handleSearch}
                    selectedRowKeys={selectedRowKeys}
                    onDelete={handleBulkDelete}
                    onAddSource={handleAddSource}
                    onUploadSources={handleUploadSources}
                    onRunCsvMigration={handleRunCsvMigration}
                    onRefreshSources={handleRefreshSources}
                    onConfigureMigration={handleBulkConfigureMigration}
                    onInspectSources={handleBulkInspectSources}
                    onConfigureInSitu={handleBulkConfigureInSitu}
                    onAddTags={handleBulkAddTags}
                    onAssignMoveGroup={handleBulkAssignMoveGroup}
                />
            </TopActionBar>
            <SourcesTable
                combinedSearch={searchQuery}
                selectedRowKeys={selectedRowKeys}
                onSelectedRowKeysChange={setSelectedRowKeys}
                bulkDeleteTriggerRef={bulkDeleteTriggerRef}
                bulkConfigureMigrationTriggerRef={bulkConfigureMigrationTriggerRef}
                bulkInspectSourcesTriggerRef={bulkInspectSourcesTriggerRef}
                addSourceModalOpen={addSourceModalOpen}
                onAddSourceModalClose={handleAddSourceModalClose}
                bulkConfigureInSituTriggerRef={bulkConfigureInSituTriggerRef}
                preflightTriggerRef={preflightTriggerRef}
                onDataRefreshNeeded={handleDataRefreshNeeded}
            />

            <BulkAddTagsModal
                open={addTagsModalOpen}
                onClose={handleAddTagsModalClose}
                selectedResources={selectedSourcesForTags}
                userOrg={userOrg}
                onSuccess={handleBulkActionSuccess}
                title="Add Tags to Selected Source(s)"
                resourceType="source"
                includeMoveGroupInfo={true}
                updateTagsByQueryParameter={true}
                apiConfig={{
                    getResourceEndpoint: (resourceId) => `/api/v3/sources/${resourceId}`,
                    updateResourceEndpoint: (resourceId) => `/api/v3/sources/${resourceId}`,
                    updateMethod: 'PATCH',
                }}
            />

            <AssignMoveGroupModal
                open={assignMoveGroupModalOpen}
                onClose={handleAssignMoveGroupModalClose}
                selectedSources={selectedSourcesForMoveGroup}
                userOrg={userOrg}
                onSuccess={handleBulkActionSuccess}
            />

            <UploadMultipleSourcesModal
                open={uploadSourcesModalOpen}
                onClose={handleUploadSourcesModalClose}
                onSuccess={handleUploadSourcesSuccess}
                userOrg={userOrg}
            />

            <CsvMigrationsUploadModal
                open={csvMigrationModalOpen}
                onClose={handleCsvMigrationModalClose}
                onSuccess={handleCsvMigrationSuccess}
                userOrg={userOrg}
            />

            <RefreshSourcesModal
                open={refreshSourcesModalOpen}
                onClose={handleRefreshSourcesModalClose}
                onSuccess={handleRefreshSourcesSuccess}
                userOrg={userOrg}
            />

            {/* CSV Migration Preflight Monitor */}
            <PreflightMonitor
                open={csvPreflightModalOpen}
                onClose={handleCsvPreflightCancel}
                onComplete={handleCsvPreflightComplete}
                preflightRequests={preflightPayload || []}
                title="Migrations Preflight"
                loadingMessage="Initiating Migrations Preflight..."
                proceedOnSuccess={true}
                migrationContext={migrationContext}
                inspectSourcesMode={false}
            />
        </div>
    );
});

Sources.displayName = 'Sources';
